import React, { useState } from 'react'
import {Star, ThumbsUp} from 'lucide-react'

interface RatingSystemProps {
  contentTitle: string
}

export function RatingSystem({ contentTitle }: RatingSystemProps) {
  const [rating, setRating] = useState(0)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [userReview, setUserReview] = useState('')
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [reviews, setReviews] = useState([
    {
      id: 1,
      name: 'Sarah M.',
      rating: 5,
      comment: 'My kids absolutely love this! The magical elements are so creative and engaging.',
      date: '2 days ago',
      helpful: 24
    },
    {
      id: 2,
      name: 'Mike R.',
      rating: 4,
      comment: 'Great nostalgic content. Brings back so many childhood memories!',
      date: '1 week ago',
      helpful: 18
    },
    {
      id: 3,
      name: 'Jennifer L.',
      rating: 5,
      comment: 'Perfect for family movie night. The animation quality still holds up amazingly well.',
      date: '2 weeks ago',
      helpful: 31
    }
  ])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (rating > 0 && userReview.trim()) {
      const newReview = {
        id: reviews.length + 1,
        name: 'You',
        rating,
        comment: userReview,
        date: 'Just now',
        helpful: 0
      }
      setReviews([newReview, ...reviews])
      setHasSubmitted(true)
      setUserReview('')
    }
  }

  const averageRating = (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
      {/* Rating Overview */}
      <div className="border-b border-gray-200 pb-6 mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Community Reviews</h3>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-5xl font-bold text-gray-800">{averageRating}</span>
            <div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= Math.round(parseFloat(averageRating))
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-1">{reviews.length} reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Your Review */}
      {!hasSubmitted ? (
        <form onSubmit={handleSubmit} className="border-b border-gray-200 pb-6 mb-6">
          <h4 className="text-xl font-bold text-gray-800 mb-4">Share Your Experience</h4>
          
          {/* Star Rating Input */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Your Rating
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Review Text */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Your Review
            </label>
            <textarea
              value={userReview}
              onChange={(e) => setUserReview(e.target.value)}
              placeholder="Share your thoughts about this magical cartoon..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none resize-none"
              rows={4}
            />
          </div>

          <button
            type="submit"
            disabled={rating === 0 || !userReview.trim()}
            className="bg-gradient-to-r from-pink-500 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            Submit Review
          </button>
        </form>
      ) : (
        <div className="border-b border-gray-200 pb-6 mb-6 bg-green-50 rounded-xl p-4">
          <p className="text-green-700 font-semibold flex items-center gap-2">
            <ThumbsUp className="w-5 h-5" />
            Thank you for your review! It helps our community find the best magical content.
          </p>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-6">
        <h4 className="text-xl font-bold text-gray-800">Recent Reviews</h4>
        {reviews.map((review) => (
          <div key={review.id} className="border-b border-gray-100 last:border-0 pb-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-gray-800">{review.name}</p>
                <div className="flex gap-1 my-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= review.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <span className="text-sm text-gray-500">{review.date}</span>
            </div>
            <p className="text-gray-700 mb-2">{review.comment}</p>
            <button className="text-sm text-gray-500 hover:text-pink-500 flex items-center gap-1 transition-colors">
              <ThumbsUp className="w-4 h-4" />
              Helpful ({review.helpful})
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
